import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Utility function para combinar classes CSS
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Função para criar URLs de páginas
export function createPageUrl(pageName) {
  return `/${pageName.toLowerCase()}`;
}

// Formatação de moeda brasileira
export function formatCurrency(value) {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

// Parse de valor monetário brasileiro (aceita vírgula e ponto)
export function parseCurrencyValue(value) {
  // Se já é número, retornar
  if (typeof value === 'number') {
    return value;
  }
  
  // Se é null, undefined ou vazio, retornar null
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  // Converter para string e limpar
  let valueStr = value.toString().trim();
  
  // Remover símbolos de moeda (R$, $, etc) e espaços
  valueStr = valueStr.replace(/[R$\s]/g, '');
  
  // Se está vazio após limpeza, retornar null
  if (valueStr === '') {
    return null;
  }
  
  // CASO 1: Formato brasileiro com vírgula (ex: 1.234,56 ou 1234,56)
  if (valueStr.includes(',')) {
    // Remover pontos (separadores de milhar)
    const withoutThousands = valueStr.replace(/\./g, '');
    // Substituir vírgula por ponto (decimal)
    const normalized = withoutThousands.replace(',', '.');
    const parsedValue = parseFloat(normalized);
    
    if (!isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  
  // CASO 2: Detectar ponto nos dois últimos dígitos (ex: 1234.56)
  const twoDigitDecimalPattern = /^[\d.,]+\.(\d{2})$/;
  const match = valueStr.match(twoDigitDecimalPattern);
  
  if (match) {
    // Remover vírgulas (possíveis separadores de milhar)
    const withoutCommas = valueStr.replace(/,/g, '');
    const parsedValue = parseFloat(withoutCommas);
    
    if (!isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  
  // CASO 3: Apenas números (sem separadores)
  const onlyNumbers = valueStr.replace(/[^\d]/g, '');
  if (onlyNumbers === valueStr) {
    const parsedValue = parseFloat(valueStr);
    if (!isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  
  // CASO 4: Tentar parse direto (formato americano padrão)
  const directParse = parseFloat(valueStr.replace(/,/g, ''));
  if (!isNaN(directParse)) {
    return directParse;
  }
  
  // Se nenhum caso funcionou, retornar null
  return null;
}

// Formatar valor para input (formato brasileiro)
export function formatCurrencyInput(value) {
  if (!value && value !== 0) return '';
  
  // Se é string, tentar parsear primeiro
  if (typeof value === 'string') {
    const parsed = parseCurrencyValue(value);
    if (parsed === null) return value; // Retornar original se não conseguir parsear
    value = parsed;
  }
  
  // Formatar com vírgula como decimal e ponto como milhar
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// Formatação de data brasileira
export function formatDate(dateString, format = 'dd/MM/yyyy') {
  if (!dateString) return '';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } catch {
    return '';
  }
}

// Formatação de CNPJ
export function formatCNPJ(cnpj) {
  if (!cnpj) return '';
  const numbers = cnpj.replace(/\D/g, '');
  if (numbers.length <= 14) {
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return cnpj;
}

// Validação de CNPJ
export function validateCNPJ(cnpj) {
  if (!cnpj) return false;
  
  const numbers = cnpj.replace(/\D/g, '');
  
  if (numbers.length !== 14) return false;
  
  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(numbers)) return false;
  
  // Validação dos dígitos verificadores
  let sum = 0;
  let weight = 2;
  
  for (let i = 11; i >= 0; i--) {
    sum += parseInt(numbers[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  
  let digit1 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  
  if (parseInt(numbers[12]) !== digit1) return false;
  
  sum = 0;
  weight = 2;
  
  for (let i = 12; i >= 0; i--) {
    sum += parseInt(numbers[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  
  let digit2 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  
  return parseInt(numbers[13]) === digit2;
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Função para calcular status automático (cliente)
export function calculateAutomaticStatus(partnership) {
  const keywords = [
    'PROCUR',
    'PRESTADO', 
    'PRESTAÇÃO',
    'PC',
    'APROVADO',
    'HOMOLOGADO',
    'PREST'
  ];

  const fieldsToCheck = [
    partnership.analises_observacoes || '',
    partnership.prazo || '',
  ];

  // Primeiro verifica se tem palavras-chave para "quitado"
  const hasKeyword = fieldsToCheck.some(field => {
    const upperField = field.toString().toUpperCase();
    return keywords.some(keyword => upperField.includes(keyword));
  });

  if (hasKeyword) {
    return 'quitado';
  }

  // Se tem data_fim, verifica prazo
  if (partnership.data_fim) {
    try {
      const dataFim = new Date(partnership.data_fim);
      const hoje = new Date();
      const diasAposVencimento = Math.floor((hoje - dataFim) / (1000 * 60 * 60 * 24));
      const PRAZO_DIAS_LIMITE = 90;
      if (diasAposVencimento <= PRAZO_DIAS_LIMITE) {
        return 'no_prazo';
      } else {
        return 'em_debito';
      }
    } catch (e) {
      // Se houver erro na data, retorna verificar
    }
  }

  return 'verificar';
}

// Função para exportar dados para CSV
export function exportToCSV(data, filename = 'export.csv') {
  if (!data || data.length === 0) {
    throw new Error('Nenhum dado para exportar');
  }

  // Obter cabeçalhos das chaves do primeiro objeto
  const headers = Object.keys(data[0]);
  
  // Criar conteúdo CSV
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escapar aspas e envolver em aspas se necessário
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    )
  ].join('\n');

  // Criar e baixar arquivo
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Função para truncar texto
export function truncateText(text, maxLength = 100) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Função para capitalizar primeira letra
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// Função para gerar cores aleatórias para gráficos
export function generateColors(count) {
  const colors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1'
  ];
  
  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(colors[i % colors.length]);
  }
  return result;
}
